import { useEffect, useRef } from 'react';

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  /** JSON-LD structured data to inject */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Set true on 404 pages to add noindex */
  noindex?: boolean;
}

const BASE_URL = 'https://go4it.agency';

/**
 * Sets per-page <title>, <meta description>, OG tags, canonical, and optional JSON-LD.
 * Properly restores previous values on unmount so no stale/missing tags remain.
 */
export function usePageMeta({ title, description, canonical, type = 'website', jsonLd, noindex }: PageMeta) {
  // Store previous values to restore on unmount
  const prevRef = useRef<{
    title: string;
    metas: { attr: string; key: string; prev: string | null }[];
    canonicalHref: string | null;
    hadCanonical: boolean;
  } | null>(null);

  useEffect(() => {
    // Capture previous state
    const prevTitle = document.title;
    const metaEntries: { attr: string; key: string; prev: string | null }[] = [];

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      const prevContent = el?.getAttribute('content') ?? null;
      metaEntries.push({ attr, key, prev: prevContent });
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    // Title
    document.title = title;

    // Core meta tags
    setMeta('name', 'description', description);
    setMeta('name', 'title', title);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    // Canonical
    const canonicalUrl = `${BASE_URL}${canonical || '/'}`;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonicalHref = link?.getAttribute('href') ?? null;
    const hadCanonical = !!link;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    setMeta('property', 'og:url', canonicalUrl);
    setMeta('name', 'twitter:url', canonicalUrl);

    // Noindex for 404 pages
    let robotsMeta: HTMLMetaElement | null = null;
    if (noindex) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      robotsMeta.content = 'noindex, nofollow';
      document.head.appendChild(robotsMeta);
    }

    // JSON-LD
    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    // Store for cleanup
    prevRef.current = { title: prevTitle, metas: metaEntries, canonicalHref: prevCanonicalHref, hadCanonical };

    return () => {
      // Restore title
      document.title = prevTitle;

      // Restore all meta tags to previous values
      for (const { attr, key, prev } of metaEntries) {
        const el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
        if (el) {
          if (prev !== null) {
            el.setAttribute('content', prev);
          }
          // Don't remove metas that existed in index.html - just restore their content
        }
      }

      // Restore canonical
      const currentLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (currentLink) {
        if (prevCanonicalHref) {
          currentLink.setAttribute('href', prevCanonicalHref);
        }
        // Don't remove the canonical - it should always exist (set in index.html)
      }

      // Remove injected elements
      scriptEl?.remove();
      robotsMeta?.remove();
    };
  }, [title, description, canonical, type, jsonLd, noindex]);
}
